import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { DFPManager } from '@edmunds/react-dfp';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';

import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { isRenderedAdUnit } from 'client/utils/ads';

import { CommonAdDefaultProps, CommonAdPropTypes } from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';
import { getPwtEnabled } from 'client/utils/pwt/get-pwt-enabled-page';

import { collapseEmptyCarouselAdSlot } from './filmstrip-carousel/utils/filmstrip-carousel-utils';
import { FilmstripCarousel } from './filmstrip-carousel/filmstrip-carousel';

import './sponsored-cars.scss';

const MAX_AD_COUNT = 4;
const CREATIVE_ID = 'edm-entry-also-viewed-vehicles-sponsored-cars';

function SponsoredCarsUI({
  isMobile,
  wrapperClassName,
  showTopDividingLine,
  showBottomDividingLine,
  showLargeArrows,
  pwtEnabled,
  verticalOffset,
  adsCustomTargeting,
  trackLoading,
  inlineStyle,
  ads11392Recipe,
  headingClass,
}) {
  const containerRef = useRef();
  const totalAdCalls = useRef(0);
  const totalAdsLoaded = useRef(0);
  const totalAdCallsRequested = useRef(0);
  const adCallsRequested = useRef([]);

  const [showFilmstrip, setShowFilmstrip] = useState(false);

  const handleSlotRenderEnd = useCallback(
    slotObj => {
      const isLoaded = isRenderedAdUnit(slotObj);

      totalAdCalls.current += 1;
      if (isLoaded) {
        totalAdsLoaded.current += 1;
      }

      if (totalAdCalls.current === MAX_AD_COUNT) {
        if (trackLoading) {
          EventToolbox.fireTrackAction({
            event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
            event_data: {
              action_name: TrackingConstant.ACTION_SHOW_CONTENT,
              action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
              subaction_name: 'sponsored_cars_displayed',
              creative_id: CREATIVE_ID,
              value: !!totalAdsLoaded.current,
            },
          });
        }
        setShowFilmstrip(totalAdsLoaded.current > 0);
        totalAdCalls.current = 0;
        totalAdsLoaded.current = 0;
        collapseEmptyCarouselAdSlot(containerRef.current, '.slick-slide');
      }
    },
    [trackLoading]
  );

  const onSlotRegister = useCallback(({ slotId }) => {
    adCallsRequested.current.push(slotId);
    totalAdCallsRequested.current += 1;
    // window.PWT?.adsPwtLoaded - see client/utils/pwt/get-pwt-bids.js
    if (totalAdCallsRequested.current % MAX_AD_COUNT === 0 && window.PWT?.adsPwtLoaded) {
      setTimeout(() => {
        DFPManager.refresh(...adCallsRequested.current);
      }, 100);
    }
  }, []);

  const pwtProps = pwtEnabled
    ? {
        skipPwtRefresh: true,
        onSlotRegister,
      }
    : {};
  const isAds11392LayoutStacked = ['chal1', 'chal2'].includes(ads11392Recipe);
  const isAds11392Layout2x2 = ['chal3', 'chal4'].includes(ads11392Recipe);
  const isADS11392Enabled = ['chal1', 'chal2', 'chal3', 'chal4'].includes(ads11392Recipe);
  const showSponsoredBuggy = ['chal1', 'chal2', 'chal4'].includes(ads11392Recipe);
  const showAdBuggy = ['chal3'].includes(ads11392Recipe);

  const renderDefaultFilmstripAdUnits = isCarousel =>
    new Array(MAX_AD_COUNT).fill().map((_, index) => (
      <div
        key={`native-ad-${index + 1}`}
        className={classNames({
          'slick-slide': isCarousel,
          'pb-0_5': isAds11392LayoutStacked,
        })}
        data-index={index}
      >
        <NativeAd
          nativeStyle="filmstrip"
          position={`${index + 1}`}
          slotRenderEndListener={handleSlotRenderEnd}
          customTargeting={adsCustomTargeting}
          {...pwtProps}
        />
      </div>
    ));

  const render2x2tFilmstripAdUnits = new Array(MAX_AD_COUNT).fill().map((_, index) => (
    <Col
      xs={6}
      key={`native-ad-${index + 1}`}
      data-index={index}
      className={classNames('pe-0 pb-0_75', {
        'ps-0_5': index % 2 === 0,
      })}
    >
      <NativeAd
        nativeStyle="filmstrip"
        position={`${index + 1}`}
        slotRenderEndListener={handleSlotRenderEnd}
        customTargeting={adsCustomTargeting}
        {...pwtProps}
      />
    </Col>
  ));

  const renderAds11392AdWrapper = isCarousel => {
    if (isAds11392Layout2x2) {
      return <Row>{render2x2tFilmstripAdUnits}</Row>;
    }
    return renderDefaultFilmstripAdUnits(isCarousel);
  };

  const renderDefaultFilmstripCarousel = (
    <FilmstripCarousel noAdsRefresh totalAdCount={MAX_AD_COUNT} showArrows={showFilmstrip}>
      {renderDefaultFilmstripAdUnits(true)}
    </FilmstripCarousel>
  );
  const sponsoredContent = (
    <div
      className={classNames('sponsored-cars', wrapperClassName, {
        'invisible d-none': !showFilmstrip,
        'is-wired': !isMobile,
        'is-mobile': isMobile,
        'show-large-arrows': showLargeArrows,
        'p-1 rounded-12': isADS11392Enabled,
        'is-ads-11293-chal1 bg-cool-gray-90': ads11392Recipe === 'chal1',
        'is-ads-11293-chal2 bg-cool-gray-90': ads11392Recipe === 'chal2',
        'is-ads-11293-chal3': ads11392Recipe === 'chal3',
        'is-ads-11293-chal4 bg-cool-gray-90': ads11392Recipe === 'chal4',
      })}
      data-tracking-parent={CREATIVE_ID}
      ref={containerRef}
      style={inlineStyle}
    >
      {showSponsoredBuggy && <div className="text-cool-gray-30 fw-medium size-12 text-end pb-0_25">Sponsored</div>}
      {showTopDividingLine && <hr className="mb-3_5" />}
      <h2
        className={classNames('mb-1_25 mb-md-2 text-start', headingClass, {
          'text-cool-gray-10': isADS11392Enabled,
        })}
      >
        Consider these recommendations
        {showAdBuggy && (
          <sup className="size-12 fw-medium text-cool-gray-50" style={{ top: '-0.5rem', left: '0.25rem' }}>
            Ad
          </sup>
        )}
      </h2>

      {isADS11392Enabled ? renderAds11392AdWrapper(false) : renderDefaultFilmstripCarousel}
      {showBottomDividingLine && <hr className="mt-2 mb-3_5" />}
    </div>
  );

  return (
    <RenderWhenViewable verticalOffset={verticalOffset} placeholder={<div className="sponsored-cars" />}>
      {sponsoredContent}
    </RenderWhenViewable>
  );
}

SponsoredCarsUI.propTypes = {
  adsCustomTargeting: CommonAdPropTypes.customTargeting,
  isMobile: PropTypes.bool,
  wrapperClassName: PropTypes.string,
  showTopDividingLine: PropTypes.bool,
  showBottomDividingLine: PropTypes.bool,
  showLargeArrows: PropTypes.bool,
  pwtEnabled: PropTypes.bool,
  trackLoading: PropTypes.bool,
  verticalOffset: PropTypes.string,
  inlineStyle: PropTypes.shape({}),
  ads11392Recipe: PropTypes.string,
  headingClass: PropTypes.string,
};

SponsoredCarsUI.defaultProps = {
  adsCustomTargeting: CommonAdDefaultProps.customTargeting,
  isMobile: false,
  wrapperClassName: '',
  showTopDividingLine: false,
  showBottomDividingLine: true,
  showLargeArrows: false,
  pwtEnabled: false,
  trackLoading: false,
  verticalOffset: '100%',
  inlineStyle: null,
  ads11392Recipe: null,
  headingClass: 'heading-3',
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  pwtEnabled: getPwtEnabled(state),
});

export const SponsoredCars = connect(mapStateToProps)(SponsoredCarsUI);
